/**
 * Buy-a-Feature prices, from each item's effort.
 *
 * The owner's rule (2026-10-28): people in the room are given money worth a
 * share (40% by default) of the total price of the items on the table, and an
 * item's price comes from its effort, mapped for each value range, varied a
 * little so that not every Medium costs the same.
 *
 * One mapping serves every value range, custom ones included: the middle of
 * the range costs $20 and each step up or down doubles or halves it — Low /
 * Medium / High is $10 / $20 / $40, XS to XL is $5 to $80. An effort recorded
 * as a bare number (no range) is priced against the session's other bare
 * numbers, the middle one costing $20. An item with no effort — every option
 * typed into a session — costs the middle price.
 *
 * The variation is fixed per session and item (a hash of the two, never a
 * random draw), so everyone in the room, guests included, sees the same price.
 */

export const BUY_A_FEATURE_MIDDLE_PRICE = 20;
/** Prices vary by up to this share either way. */
export const BUY_A_FEATURE_VARIATION = 0.15;

/** An item's official effort as stored: the value and the range it was given on. */
export interface EffortReading {
  value: unknown;
  scaleId?: string | null;
}

export interface EffortScalePoint {
  value: number;
  label?: string;
}

export interface EffortScale {
  id: string;
  points?: EffortScalePoint[];
  isDefault?: boolean;
}

/** The effort attribute's value ranges (attributes.metadata.scales, options). */
export interface EffortAttribute {
  scales?: EffortScale[] | null;
  defaultScaleId?: string | null;
  options?: string[] | null;
}

function effortValueText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'object') {
    const o = value as { label?: unknown; value?: unknown };
    return effortValueText(o.label ?? o.value);
  }
  return null;
}

function scaleFor(attribute: EffortAttribute | null | undefined, scaleId: string | null | undefined): EffortScale | null {
  const scales = (attribute?.scales ?? []).filter((s) => (s.points?.length ?? 0) > 0);
  return (
    (scaleId ? scales.find((s) => s.id === scaleId) : undefined) ??
    scales.find((s) => s.id === attribute?.defaultScaleId) ??
    scales.find((s) => s.isDefault) ??
    null
  );
}

/** Steps from the middle of an ordered list of n positions. */
function stepsFrom(index: number, count: number): number {
  return index - (count - 1) / 2;
}

/**
 * Where an effort sits: steps from the middle of its range, or a bare number
 * to be priced against the others. Null when there is no effort.
 */
export function effortPosition(
  reading: EffortReading | null | undefined,
  attribute: EffortAttribute | null | undefined,
): { steps: number } | { bare: number } | null {
  const label = effortValueText(reading?.value);
  if (label == null) return null;
  const scale = scaleFor(attribute, reading?.scaleId);
  const asNumber = Number(label);

  if (scale?.points?.length) {
    const points = [...scale.points].sort((a, b) => a.value - b.value);
    const byLabel = points.findIndex((p) => p.label?.toLowerCase() === label.toLowerCase());
    if (byLabel >= 0) return { steps: stepsFrom(byLabel, points.length) };
    if (Number.isFinite(asNumber)) {
      // The nearest point, so a value just outside the range still prices.
      let nearest = 0;
      points.forEach((p, i) => {
        if (Math.abs(p.value - asNumber) < Math.abs(points[nearest].value - asNumber)) nearest = i;
      });
      return { steps: stepsFrom(nearest, points.length) };
    }
  }

  const options = attribute?.options ?? [];
  const byOption = options.findIndex((o) => o.toLowerCase() === label.toLowerCase());
  if (byOption >= 0) return { steps: stepsFrom(byOption, options.length) };

  if (Number.isFinite(asNumber) && asNumber > 0) return { bare: asNumber };
  return null;
}

/** FNV-1a: a fixed number in [0, 1) for a string. */
function unitHash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 0x100000000;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Whole-dollar prices keyed by item id. `seed` is the session id: the same
 * session and item always get the same price.
 */
export function priceItemsByEffort(
  itemIds: readonly string[],
  efforts: Readonly<Record<string, EffortReading | undefined>>,
  attribute: EffortAttribute | null | undefined,
  seed: string,
): Record<string, number> {
  const positions = new Map(itemIds.map((id) => [id, effortPosition(efforts[id], attribute)]));
  const bare = [...positions.values()].flatMap((p) => (p && 'bare' in p ? [p.bare] : []));
  const middleBare = bare.length ? median(bare) : 1;

  const prices: Record<string, number> = {};
  for (const id of itemIds) {
    const position = positions.get(id) ?? null;
    const base =
      position == null
        ? BUY_A_FEATURE_MIDDLE_PRICE
        : 'steps' in position
          ? BUY_A_FEATURE_MIDDLE_PRICE * 2 ** position.steps
          : BUY_A_FEATURE_MIDDLE_PRICE * (position.bare / middleBare);
    const variation = 1 + (2 * unitHash(`${seed}:${id}`) - 1) * BUY_A_FEATURE_VARIATION;
    prices[id] = Math.max(1, Math.round(base * variation));
  }
  return prices;
}

/**
 * Pooling (owner, 2026-10-28): in a live session a person pledges toward an
 * item rather than buying it whole, so several people can fund an item none
 * of them could afford alone. An item is bought when the room's pledges reach
 * its price. Pledges move in $5 steps; one person never pledges more than the
 * item's price, nor more than they have left.
 */
export const BUY_A_FEATURE_PLEDGE_STEP = 5;

/** The pledge after one step up: $5 more, but not past the price or what is left. */
export function raisePledge(current: number, price: number, remaining: number): number {
  return current + Math.max(0, Math.min(BUY_A_FEATURE_PLEDGE_STEP, price - current, remaining));
}

/** The pledge after one step down: to the $5 mark below, never under zero. */
export function lowerPledge(current: number): number {
  if (current <= 0) return 0;
  const below = Math.ceil(current / BUY_A_FEATURE_PLEDGE_STEP) * BUY_A_FEATURE_PLEDGE_STEP - BUY_A_FEATURE_PLEDGE_STEP;
  return Math.max(0, below);
}

/** How an item stands when everyone's pledges are added up. */
export function pooledLine(pledged: number, price: number): string {
  return pledged >= price ? `$${pledged} of $${price} · funded` : `$${pledged} of $${price}`;
}

/** The money each person is given: `share` of the total price, in whole dollars. */
export function buyAFeatureBudget(prices: Readonly<Record<string, number>>, share: number): number {
  const total = Object.values(prices).reduce((sum, p) => sum + p, 0);
  return Math.round(total * share);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Each item's official effort (the workspace's accepted value, no session
 * scope), keyed by item id. Typed options are not items and are skipped. A
 * member reads this directly; a guest is handed it by the guest room
 * (`get_guest_activity`, migration 964). Best-effort: a failure prices every
 * item at the middle price.
 */
export async function fetchOfficialEfforts(itemIds: readonly string[]): Promise<Record<string, EffortReading>> {
  const realIds = itemIds.filter((id) => UUID_RE.test(id));
  if (realIds.length === 0) return {};
  try {
    const { getSafeSupabaseClient } = await import('./supabaseSync');
    const supabase = getSafeSupabaseClient();
    if (!supabase) return {};
    const { data, error } = await supabase
      .from('accepted_values')
      .select('item_id, value, scale_id')
      .in('item_id', realIds)
      .eq('attribute_key', 'effort')
      .is('session_scope', null);
    if (error || !data) return {};
    return Object.fromEntries(
      (data as Array<{ item_id: string; value: unknown; scale_id: string | null }>).map((row) => [
        row.item_id,
        { value: row.value, scaleId: row.scale_id },
      ]),
    );
  } catch {
    return {};
  }
}

/** The effort attribute as the attributes store holds it, in the shape pricing reads. */
export function toEffortAttribute(
  definition: { scales?: EffortScale[] | null; defaultScaleId?: string | null; options?: string[] | null } | null | undefined,
): EffortAttribute | null {
  if (!definition) return null;
  return {
    scales: definition.scales ?? [],
    defaultScaleId: definition.defaultScaleId ?? null,
    options: definition.options ?? null,
  };
}

/**
 * A session's prices for a signed-in member or host: reads each item's
 * official effort, then prices. The same session and items give the same
 * prices a guest is shown.
 */
export async function loadSessionPrices(
  itemIds: readonly string[],
  sessionId: string,
  attribute: EffortAttribute | null,
): Promise<Record<string, number>> {
  return priceItemsByEffort(itemIds, await fetchOfficialEfforts(itemIds), attribute, sessionId);
}
